import SplitButton from '@common/components/SplitButton/SplitButton'
import { ReadTaskSummaryData } from '@common/config/api/client'
import { CHECKBOX_WIDTH, COLUMN_WIDTH, MIN_VISIBLE_TIMEPOINTS } from '@features/reader_summary/pages/constants'
import { TableScrollSize } from '@features/reader_summary/pages/summary.types'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import AdjudicationDecisionModal from './AdjudicationDecisionModal'
import ReaderSummaryTable from './ReaderAdjudicationTable'
import { useGetAdjudicationJustifications } from '../../hooks/useGetAdjudicationJustifications'

export default function ReaderAdjudicationLayout({
  timepoints,
  readTaskId,
  read_task_summary_metadata,
  onClose,
  onFocusChild,
  initialModalStep,
  readerSetEndTime,
  pauseReaderActiveTime,
  isRawDataEnabled,
}: {
  timepoints: ReadTaskSummaryData['timepoints']
  readTaskId: string
  read_task_summary_metadata: ReadTaskSummaryData['read_task_summary_metadata']
  onClose?: () => void
  onFocusChild?: (index: number, forcedReaderTaskId?: string) => void
  initialModalStep?: string
  readerSetEndTime?: (end_time: Date | number) => void
  pauseReaderActiveTime?: () => void
  isRawDataEnabled?: boolean
}) {
  const [tableScrollSize, setTableScrollSize] = useState<TableScrollSize>({
    columnWidth: COLUMN_WIDTH,
    minVisibleTimepoints: MIN_VISIBLE_TIMEPOINTS,
    emptyCellsCount: 0,
    firstTimepointLeftOffset: COLUMN_WIDTH,
    lastTimepointLeftOffset: 0,
    checkboxWidth: CHECKBOX_WIDTH,
    isScrolling: false,
  })

  const { studyId } = useParams<{ studyId: string }>()
  const { data: justifications } = useGetAdjudicationJustifications(['ReadTasksAdjudicationJustifications'], {
    study_id: studyId!,
  })

  const lastTimepoint = timepoints[timepoints.length - 1]
  const options = lastTimepoint.reader_submissions
    .filter((submission) => submission.group_name)
    .sort((a, b) => (a.group_name || '').localeCompare(b.group_name || ''))
    .map((submission) => ({
      label: `Go to ${submission.group_name}`,
      readTaskId: submission.read_task_id,
    }))

  useEffect(() => {
    const handleResize = () => {
      const scrollBarSize = window.innerWidth - document.body.offsetWidth
      const availableWidth = window.innerWidth - scrollBarSize - 128 - CHECKBOX_WIDTH

      const potentialTimepoints = Math.floor(availableWidth / (COLUMN_WIDTH + 0.4)) - 1
      const newMinVisibleTimepoints = Math.max(potentialTimepoints, 3)

      const newColumnWidth = availableWidth / (newMinVisibleTimepoints + 1) - 0.4

      const totalTimepointColumns = Math.max(newMinVisibleTimepoints, timepoints.length)
      const emptyCellsCount = totalTimepointColumns - timepoints.length
      const firstTimepointLeftOffset = newColumnWidth + emptyCellsCount * newColumnWidth
      const lastTimepointLeftOffset = firstTimepointLeftOffset + (newMinVisibleTimepoints - 1) * newColumnWidth

      const isScrolling = newMinVisibleTimepoints < timepoints.length

      setTableScrollSize((prev) => ({
        ...prev,
        lastTimepointLeftOffset,
        emptyCellsCount,
        firstTimepointLeftOffset,
        columnWidth: newColumnWidth,
        minVisibleTimepoints: totalTimepointColumns,
        isScrolling,
      }))
    }

    window.addEventListener('resize', handleResize)

    const observer = new ResizeObserver(handleResize)
    observer.observe(document.body)

    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      observer.disconnect()
    }
  }, [timepoints])
  const theme = useTheme()

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          zIndex: 20,
        }}
      >
        <Stack
          direction="row"
          paddingX={4}
          justifyContent="space-between"
          alignItems="center"
          height={56}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack>
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
              }}
            >
              {read_task_summary_metadata.timepoint_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Task ID {read_task_summary_metadata.task_pseudo_id}
            </Typography>
          </Stack>
          <Stack>
            <Button variant="text" onClick={onClose}>
              <Typography
                variant="button"
                sx={{
                  color: 'text.primary',
                }}
              >
                Close
              </Typography>
            </Button>
          </Stack>
        </Stack>
      </Box>
      {read_task_summary_metadata.adjudication_justification_id && (
        <Stack paddingX={8} paddingTop={10} paddingBottom={4}>
          <Box
            sx={{
              overflowX: tableScrollSize.isScrolling ? 'auto' : 'hidden',
              bgcolor: 'background.default',

              borderRadius: 4,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              spacing={2}
              sx={{
                position: 'sticky',
                left: 0,
                backgroundColor: 'background.paper',
                paddingLeft: 3,
                paddingTop: 3,
                paddingBottom: 3,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="h6">Adjudication Decision</Typography>
              <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary">
                  Status: {read_task_summary_metadata.adjudication_agreed ? 'Agreed' : 'Disagreed'}
                </Typography>
                {read_task_summary_metadata.adjudication_selected_read_task_id && (
                  <Typography variant="body2" color="text.secondary">
                    Selected Reader:{' '}
                    {lastTimepoint.reader_submissions.find(
                      (submission) =>
                        submission.read_task_id === read_task_summary_metadata.adjudication_selected_read_task_id,
                    )?.group_name || read_task_summary_metadata.adjudication_selected_read_task_id}
                  </Typography>
                )}
                {read_task_summary_metadata.adjudication_justification_id && (
                  <Typography variant="body2" color="text.secondary">
                    Justification:{' '}
                    {justifications?.data?.find(
                      (justification) => justification.id === read_task_summary_metadata.adjudication_justification_id,
                    )?.justification || read_task_summary_metadata.adjudication_justification_id}
                  </Typography>
                )}
                {read_task_summary_metadata.adjudication_comment && (
                  <Typography variant="body2" color="text.secondary">
                    Comment: {read_task_summary_metadata.adjudication_comment}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      )}
      <Stack paddingX={8} paddingTop={2} paddingBottom={10}>
        <ReaderSummaryTable
          timepoints={timepoints}
          readTaskSummaryMetaData={read_task_summary_metadata}
          tableScrollSize={tableScrollSize}
        />
      </Stack>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: '100%',
          zIndex: 20,
        }}
      >
        <Stack
          direction="row"
          paddingX={4}
          justifyContent="space-between"
          alignItems="center"
          height={56}
          sx={{
            backgroundColor: 'background.default',
          }}
        >
          <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="left">
            <SplitButton
              options={options.map((opt) => opt.label)}
              onFocusChild={(index: number, forcedReaderTaskId?: string) => {
                const taskId = forcedReaderTaskId || options[index]?.readTaskId
                if (taskId) onFocusChild?.(index, taskId)
              }}
              isRawDataEnabled={isRawDataEnabled}
            />
          </Stack>
          <Stack>
            <AdjudicationDecisionModal
              taskId={readTaskId}
              timepoint={timepoints[timepoints.length - 1]}
              initialModalStep={initialModalStep}
              disabled={!!read_task_summary_metadata.adjudication_justification_id}
              readerSetEndTime={readerSetEndTime}
              pauseReaderActiveTime={pauseReaderActiveTime}
            />
          </Stack>
        </Stack>
      </Box>
    </>
  )
}
